import { motion, useScroll, useTransform } from "framer-motion";
import { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, GraduationCap, Sparkles, Heart, Brain, Sprout, BookOpen, Baby, Rocket, School, HandHeart, MessageSquare, ShieldCheck, Users, Layers, Compass, TrendingUp } from "lucide-react";
import RevealText from "../components/RevealText";
import { BRAND } from "../lib/brand";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Banner verso la landing "Become Tata Like a Pro": appare solo quando la pagina è pubblicata dall'admin. */
function TataProBanner() {
  const [live, setLive] = useState(false);
  useEffect(() => {
    axios.get(`${API}/tata-landing/status`).then((r) => setLive(!!r.data?.published)).catch(() => {});
  }, []);
  if (!live) return null;
  return (
    <section className="px-6 md:px-10 py-16 bg-[#FFE4E8]/50" data-testid="academy-tatapro-banner">
      <div className="max-w-4xl mx-auto flex flex-col md:flex-row items-center gap-8 text-center md:text-left">
        <img src={BRAND.logo} alt="" className="h-24 w-24 rounded-full shadow-lg shrink-0" />
        <div className="flex-1">
          <p className="text-[10px] uppercase tracking-[0.4em] text-[#FF2768] font-semibold mb-2">Il percorso completo</p>
          <h2 className="font-display text-3xl md:text-4xl text-[#3D2140] mb-2">Become Tata Like a Pro</h2>
          <p className="text-[#3D2140]/70">22 ore di formazione online in diretta: 10 moduli, casi educativi e strumenti pratici, in piccolo gruppo o individuale.</p>
        </div>
        <Link to="/become-tata-like-a-pro" data-testid="academy-tatapro-link" className="btn-tv-primary shrink-0">
          Scopri il corso <ArrowRight size={15} />
        </Link>
      </div>
    </section>
  );
}

const EXPERIENCE = [
  {
    icon: Sparkles,
    title: "Esperienza pluriennale",
    body: "Oltre 22 anni dedicati alla cura dei bambini, lavorando a fianco delle famiglie per comprendere esigenze e sfide quotidiane.",
  },
  {
    icon: GraduationCap,
    title: "Formazione completa",
    body: "Percorsi formativi costruiti sulla pratica reale, per trasmettere tecniche e strategie efficaci nella cura dei bambini.",
  },
];

const GENTLE_TOPICS = [
  {
    icon: Heart,
    title: "Ascoltare i bambini",
    body: "Impara ad ascoltare le esigenze dei bambini, a riconoscere i loro segnali e a comunicare in modo efficace per creare un rapporto di fiducia.",
  },
  {
    icon: Brain,
    title: "Sviluppo emotivo",
    body: "Scopri come favorire la crescita emotiva dei bambini, sostenendo autostima, empatia e capacità di gestire le emozioni.",
  },
  {
    icon: Sprout,
    title: "Sviluppo delle competenze",
    body: "Acquisisci le competenze per accompagnare i bambini nella crescita, sostenendo autonomia e indipendenza.",
  },
];

const STATS = [
  { n: "22", label: "Anni di esperienza", desc: "Percorsi formativi costruiti su oltre 22 anni come tata professionista." },
  { n: "100+", label: "Famiglie supportate", desc: "Ho accompagnato oltre 100 famiglie a crescere i loro bambini con serenità e amore." },
  { n: "1000+", label: "Ore di formazione", desc: "Oltre 1000 ore di formazione in diverse aree della cura dei bambini." },
];

const MONTESSORI = [
  { icon: BookOpen, tag: "Principi pedagogici", title: "Le fondamenta", body: "Esplorazione dei principi fondamentali del Metodo Montessori e di come integrarli nel tuo approccio professionale alla cura dei bambini." },
  { icon: Baby, tag: "0–12 mesi", title: "Attività per neonati", body: "Proposte pratiche e stimolanti adatte ai più piccoli, che favoriscono lo sviluppo sensoriale e motorio nel primo anno di vita." },
  { icon: Rocket, tag: "1–3 anni", title: "Attività per bambini", body: "Idee creative per accompagnare i bambini nella fase di esplorazione e conquista dell'autonomia, rispettando i loro tempi e interessi." },
  { icon: School, tag: "3–6 anni", title: "Attività prescolare", body: "Progetti e attività strutturate per preparare i bambini all'esperienza scolastica, sviluppando competenze cognitive, sociali ed emotive." },
];

const COMMUNICATION = [
  { icon: HandHeart, title: "Costruire fiducia", body: "Strategie per stabilire relazioni di fiducia con i genitori, creando un dialogo aperto e costruttivo dal primo incontro e mantenendolo nel tempo." },
  { icon: MessageSquare, title: "Comunicazione quotidiana", body: "Tecniche e strumenti per condividere informazioni sulla giornata del bambino, celebrare i progressi e affrontare in modo professionale eventuali difficoltà." },
  { icon: ShieldCheck, title: "Situazioni delicate", body: "Come affrontare conversazioni difficili, stabilire confini professionali chiari e risolvere potenziali conflitti in modo rispettoso ed efficace." },
];

const PATH = [
  { icon: Layers, title: "Formazione completa", body: "Undici incontri da 2 ore — 22 ore complessive — che coprono tutti gli aspetti fondamentali della professione, dalle competenze pedagogiche agli aspetti legali e contrattuali." },
  { icon: Compass, title: "Approccio flessibile", body: "Scegli tra formazione individuale o di gruppo, adattando il percorso alle tue esigenze specifiche e al tuo stile di apprendimento." },
  { icon: Users, title: "Supporto continuo", body: "Accesso a risorse e materiali didattici per continuare ad approfondire la formazione anche dopo il completamento del percorso." },
  { icon: TrendingUp, title: "Crescita professionale", body: "Opportunità concrete per migliorare il tuo posizionamento nel mercato e offrire una cura di qualità superiore riconosciuta dalle famiglie." },
];

export default function Academy() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bearY = useTransform(scrollYProgress, [0, 1], ["0%", "70%"]);
  const bearRotate = useTransform(scrollYProgress, [0, 1], [0, 25]);

  return (
    <div data-testid="academy-page">
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-[90vh] pt-32 md:pt-40 pb-16 px-6 md:px-10 overflow-hidden">
        <div className="max-w-[1600px] mx-auto relative">
          <p className="text-[10px] uppercase tracking-[0.4em] text-[#FF2768] mb-4">— Formazione per professionisti dell'infanzia</p>
          <h1 className="font-display text-[16vw] md:text-[13vw] lg:text-[16rem] leading-[0.82] text-[#3D2140]">
            <RevealText text={["T.A.T.A.", <span key="1" className="text-[#FF2768]">Academy.</span>]} stagger={0.14} />
          </h1>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-12 gap-8">
            <p className="md:col-span-6 md:col-start-4 font-editorial text-2xl md:text-3xl text-[#3D2140] leading-snug italic">
              Scopri il mio metodo per diventare un professionista dell'infanzia capace di offrire <strong>cura, supporto ed educazione rispettosa</strong>.
            </p>
          </div>
          <div className="mt-10 md:col-span-6 md:col-start-4">
            <a href="#programma" data-testid="hero-cta-scopri" className="btn-tv-primary">
              Scopri di più <ArrowRight size={16} />
            </a>
          </div>
        </div>

        <motion.img
          src={BRAND.logo}
          alt=""
          aria-hidden
          style={{ y: bearY, rotate: bearRotate }}
          className="absolute right-4 md:right-16 top-24 md:top-40 h-32 md:h-56 w-32 md:w-56 rounded-full opacity-80"
        />
      </section>

      {/* ESPERIENZA */}
      <section id="programma" className="px-6 md:px-10 py-24 md:py-32 bg-[#81C3C9]/25 scroll-mt-24" data-testid="experience-section">
        <div className="max-w-[1600px] mx-auto">
          <div className="mb-16 max-w-3xl">
            <p className="text-[10px] uppercase tracking-[0.4em] text-[#FF2768] mb-4">— L'esperienza al centro</p>
            <h2 className="font-display text-6xl md:text-8xl text-[#3D2140] leading-[0.9]">
              L'esperienza<br/>al centro<br/><span className="text-[#FF2768]">della mia Academy.</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {EXPERIENCE.map((e, i) => {
              const Icon = e.icon;
              return (
                <motion.div
                  key={e.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.6, delay: i * 0.1 }}
                  className={`p-8 md:p-12 rounded-[32px] bg-white ${i === 1 ? "md:mt-16" : ""}`}
                  data-testid={`experience-${i}`}
                >
                  <span className="h-16 w-16 rounded-2xl bg-[#FF2768] text-white flex items-center justify-center mb-6">
                    <Icon size={24} />
                  </span>
                  <h3 className="font-display text-4xl md:text-5xl text-[#3D2140] mb-4">{e.title}</h3>
                  <p className="text-[#3D2140]/75 leading-relaxed text-lg">{e.body}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* EDUCAZIONE GENTILE */}
      <section className="px-6 md:px-10 py-24 md:py-32 max-w-[1600px] mx-auto" data-testid="gentle-education-section">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-20">
          <div className="md:col-span-7">
            <p className="text-[10px] uppercase tracking-[0.4em] text-[#FF2768] mb-4">— L'Educazione gentile</p>
            <h2 className="font-display text-6xl md:text-8xl text-[#3D2140] leading-[0.9]">
              Un approccio<br/><span className="text-[#FF2768]">rispettoso.</span>
            </h2>
          </div>
          <div className="md:col-span-5 self-end">
            <p className="text-lg text-[#3D2140]/75 leading-relaxed font-editorial italic">
              Ascolto, presenza, competenza. Tre parole che diventano gesti quotidiani nella cura dei bambini.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {GENTLE_TOPICS.map((t, i) => {
            const Icon = t.icon;
            return (
              <motion.div
                key={t.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                className={`p-8 rounded-3xl border border-[#81C3C9]/40 bg-white hover:border-[#FF2768] transition-colors duration-500 ${i === 1 ? "md:mt-12" : i === 2 ? "md:mt-24" : ""}`}
                data-testid={`gentle-education-topic-${i}`}
              >
                <span className="h-14 w-14 rounded-2xl bg-[#FF2768] text-white flex items-center justify-center mb-6">
                  <Icon size={22} />
                </span>
                <h3 className="font-display text-3xl md:text-4xl text-[#3D2140] mb-4">{t.title}</h3>
                <p className="text-[#3D2140]/75 leading-relaxed">{t.body}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* MONTESSORI */}
      <section className="px-6 md:px-10 py-24 md:py-32 bg-[#FAF8F5]" data-testid="montessori-section">
        <div className="max-w-[1600px] mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-20">
            <div className="md:col-span-7">
              <p className="text-[10px] uppercase tracking-[0.4em] text-[#FF2768] mb-4">— Il Metodo Montessori</p>
              <h2 className="font-display text-6xl md:text-8xl text-[#3D2140] leading-[0.9]">
                Nella pratica<br/><span className="text-[#FF2768]">quotidiana.</span>
              </h2>
            </div>
            <div className="md:col-span-5 self-end">
              <p className="text-lg text-[#3D2140]/75 leading-relaxed font-editorial italic">
                Dai principi alle attività, un percorso completo per accompagnare ogni fascia d'età con proposte pensate, coerenti e rispettose.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {MONTESSORI.map((m, i) => {
              const Icon = m.icon;
              return (
                <motion.div
                  key={m.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.25 }}
                  transition={{ duration: 0.6, delay: (i % 4) * 0.06 }}
                  className={`p-7 rounded-3xl bg-white border border-[#81C3C9]/40 hover:border-[#FF2768] transition-colors duration-500 ${i % 2 === 1 ? "lg:mt-10" : ""}`}
                  data-testid={`montessori-${i}`}
                >
                  <span className="h-12 w-12 rounded-2xl bg-[#FF2768] text-white flex items-center justify-center mb-5">
                    <Icon size={20} />
                  </span>
                  <p className="text-[10px] uppercase tracking-[0.3em] text-[#81C3C9] font-semibold mb-2">{m.tag}</p>
                  <h3 className="font-display text-3xl text-[#3D2140] mb-3">{m.title}</h3>
                  <p className="text-[#3D2140]/75 text-sm leading-relaxed">{m.body}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* COMUNICAZIONE */}
      <section className="px-6 md:px-10 py-24 md:py-32 max-w-[1600px] mx-auto" data-testid="communication-section">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-20">
          <div className="md:col-span-7">
            <p className="text-[10px] uppercase tracking-[0.4em] text-[#FF2768] mb-4">— Con le famiglie</p>
            <h2 className="font-display text-6xl md:text-8xl text-[#3D2140] leading-[0.9]">
              Comunicare<br/><span className="text-[#FF2768]">efficacemente.</span>
            </h2>
          </div>
          <div className="md:col-span-5 self-end">
            <p className="text-lg text-[#3D2140]/75 leading-relaxed font-editorial italic">
              La relazione con i genitori è parte del lavoro. Impara a costruirla, coltivarla e proteggerla con professionalità e umanità.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {COMMUNICATION.map((c, i) => {
            const Icon = c.icon;
            return (
              <motion.div
                key={c.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                className={`p-8 rounded-3xl border border-[#81C3C9]/40 bg-white hover:border-[#FF2768] transition-colors duration-500 ${i === 1 ? "md:mt-12" : ""}`}
                data-testid={`communication-${i}`}
              >
                <span className="h-14 w-14 rounded-2xl bg-[#81C3C9] text-white flex items-center justify-center mb-6">
                  <Icon size={22} />
                </span>
                <h3 className="font-display text-3xl md:text-4xl text-[#3D2140] mb-4">{c.title}</h3>
                <p className="text-[#3D2140]/75 leading-relaxed">{c.body}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* PERCORSO PROFESSIONALE */}
      <section className="px-6 md:px-10 py-24 md:py-32 bg-[#81C3C9]/25" data-testid="path-section">
        <div className="max-w-[1600px] mx-auto">
          <div className="mb-20 max-w-3xl">
            <p className="text-[10px] uppercase tracking-[0.4em] text-[#FF2768] mb-4">— Inizia ora</p>
            <h2 className="font-display text-6xl md:text-8xl text-[#3D2140] leading-[0.9]">
              Il tuo percorso<br/><span className="text-[#FF2768]">professionale.</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {PATH.map((p, i) => {
              const Icon = p.icon;
              return (
                <motion.div
                  key={p.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.25 }}
                  transition={{ duration: 0.6, delay: (i % 2) * 0.08 }}
                  className={`p-8 md:p-10 rounded-[32px] bg-white ${i % 2 === 1 ? "md:mt-16" : ""}`}
                  data-testid={`path-${i}`}
                >
                  <div className="flex items-baseline gap-4 mb-6">
                    <span className="font-editorial text-[#FF2768] text-lg">0{i + 1}</span>
                    <div className="h-px flex-1 bg-[#3D2140]/15" />
                  </div>
                  <span className="h-14 w-14 rounded-2xl bg-[#FF2768] text-white flex items-center justify-center mb-6">
                    <Icon size={22} />
                  </span>
                  <h3 className="font-display text-4xl md:text-5xl text-[#3D2140] mb-4">{p.title}</h3>
                  <p className="text-[#3D2140]/75 leading-relaxed">{p.body}</p>
                </motion.div>
              );
            })}
          </div>

          <div className="mt-16 text-center">
            <p className="text-[#3D2140]/70 mb-6 text-lg font-editorial italic">Richiedi una consulenza personalizzata</p>
            <Link to="/prenota" data-testid="path-cta-booking" className="btn-tv-primary">
              Contattami <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* LANDING COURSE LINK — visibile solo se la landing è pubblicata */}
      <TataProBanner />

      {/* STATS */}
      <section className="relative px-6 md:px-10 py-24 md:py-32 bg-[#3D2140] text-white overflow-hidden" data-testid="stats-section">
        <div className="absolute -left-24 -top-24 h-96 w-96 rounded-full bg-[#FF2768]/15 blur-3xl pointer-events-none" />
        <div className="absolute -right-24 -bottom-24 h-96 w-96 rounded-full bg-[#81C3C9]/15 blur-3xl pointer-events-none" />
        <div className="max-w-[1600px] mx-auto relative">
          <div className="mb-16 max-w-3xl">
            <p className="text-[10px] uppercase tracking-[0.4em] text-[#FFE45C] mb-4">— Unisciti a me</p>
            <h2 className="font-display text-6xl md:text-8xl lg:text-9xl leading-[0.85]">
              In questa<br/><span className="text-[#FF2768]">avventura.</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
            {STATS.map((s, i) => (
              <motion.div
                key={s.label}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.7, delay: i * 0.1 }}
                className="border-t-2 border-[#FF2768] pt-8"
                data-testid={`stat-${i}`}
              >
                <p className="font-display text-[7rem] md:text-[9rem] leading-[0.85] text-white">
                  {s.n}
                </p>
                <p className="text-sm uppercase tracking-[0.35em] text-[#FFE45C] mt-2 mb-4">{s.label}</p>
                <p className="text-white/70 leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>

          <div className="mt-16 flex flex-wrap gap-3">
            <Link to="/prenota" data-testid="academy-cta-booking" className="btn-tv-primary">
              Contattami per la formazione <ArrowRight size={16} />
            </Link>
            <a href={BRAND.whatsappUrl} target="_blank" rel="noopener noreferrer" data-testid="academy-cta-whatsapp" className="inline-flex items-center gap-2 px-7 py-[14px] rounded-full border border-white/40 text-white hover:bg-white hover:text-[#3D2140] transition-all">
              Info su WhatsApp
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
